import React, { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import TitleContainer from "../../components/common/Layout/TitleContainer";
import {
  InfoInputScreen,
  InfoCompleteScreen,
  InfoEditScreen,
  InfoExpiredScreen,
  DeliveryCompleteScreen,
} from "../../components/NewYear/Winner";
import { LoadingSpinner } from "../../components/NewYear/common";
import { useWinnerInfo } from "../../hooks/newyear";
import {
  WINNER_INFO_STATUS,
  DELIVERY_STATUS,
} from "../../constants/newyearConstants";

const WinnerInfoPage = () => {
  const { eventId } = useParams();
  const [isEditing, setIsEditing] = useState(false);

  const {
    winnerInfo,
    loading,
    submitting,
    error,
    fetchWinnerInfo,
    submit,
    update,
    canModify,
    getRemainingModifications,
    isExpired,
    isSubmitted,
    isPendingSubmission,
  } = useWinnerInfo(eventId);

  useEffect(() => {
    if (eventId) {
      fetchWinnerInfo();
    }
  }, [eventId, fetchWinnerInfo]);

  const handleSubmit = useCallback(
    async ({ name, phone }) => {
      try {
        await submit(name, phone);
      } catch (err) {
        console.error("Submit error:", err);
      }
    },
    [submit]
  );

  const handleUpdate = useCallback(
    async ({ name, phone, reason }) => {
      try {
        await update(name, phone, reason);
        setIsEditing(false);
      } catch (err) {
        console.error("Update error:", err);
      }
    },
    [update]
  );

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleGoHome = useCallback(() => {
    window.location.href = "/";
  }, []);

  const renderContent = () => {
    if (loading) {
      return <LoadingSpinner message="정보를 불러오는 중..." />;
    }

    if (error) {
      const errorMessage = typeof error === "string"
        ? error
        : error?.response?.data?.message || error?.message || "정보를 불러오는데 실패했습니다.";
      return (
        <div className="newyear-winner-page__error">
          <p>{errorMessage}</p>
        </div>
      );
    }

    if (isExpired()) {
      return <InfoExpiredScreen onGoHome={handleGoHome} />;
    }

    if (winnerInfo?.deliveryStatus === DELIVERY_STATUS.DELIVERED) {
      return <DeliveryCompleteScreen winnerInfo={winnerInfo} />;
    }

    if (isSubmitted() && isEditing && canModify()) {
      return (
        <InfoEditScreen
          currentInfo={winnerInfo}
          onSubmit={handleUpdate}
          onCancel={handleCancelEdit}
          loading={submitting}
          remainingModifications={getRemainingModifications()}
        />
      );
    }

    if (isSubmitted()) {
      return (
        <InfoCompleteScreen
          winnerInfo={winnerInfo}
          onEdit={handleEdit}
          canEdit={canModify()}
          remainingModifications={getRemainingModifications()}
        />
      );
    }

    if (isPendingSubmission()) {
      return (
        <InfoInputScreen
          deadline={winnerInfo?.deadline}
          onSubmit={handleSubmit}
          loading={submitting}
          eventName={winnerInfo?.eventName}
        />
      );
    }

    return (
      <div className="newyear-winner-page__not-found">
        <p>당첨 정보를 찾을 수 없습니다.</p>
      </div>
    );
  };

  return (
    <>
      <TitleContainer
        title="당첨자 정보 입력"
        description="경품 수령을 위한 정보를 입력해주세요"
        style={{ "bkg-grey-ultralight": true }}
      />
      <div className="section-block clearfix pt-0 pb-0 bkg-grey-ultralight">
        <div className="row">
          <div className="column width-10 offset-1 content-inner">
            <div className="newyear-winner-page">{renderContent()}</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WinnerInfoPage;
